import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Plus, Search, Upload, FileText, CheckCircle, AlertTriangle,
  ChevronRight, ChevronDown, MessageCircle, X, Send, Download, Edit3,
  Minus, Check, Clock, DollarSign, Droplets, Wind, Thermometer, Info
} from 'lucide-react';
import { colors, Card, Button, Header } from '../shared';
import { useInsuranceData } from '../../contexts/InsuranceDataContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  MITIGATION_RATES,
  WATER_CATEGORIES,
  LABOR_SPLITS,
  FORMULAS,
  DRYING_DAYS_GUIDANCE,
  calculateEquipmentRecommendations,
  calculateMitigationLineItems,
  getDefaultRates,
  getDefaultMitigationValues,
  createEmptyEstimate,
} from '../../data/insuranceEstimatorConstants';
import { parseEstimateDocument, parseEstimateWithVision, processClarification, analyzeRepairLineItems } from '../../utils/openai';
import { generateXLS, generatePDF } from '../../utils/insuranceEstimateExport';

// ============================================
// STEP INDICATOR (allows jumping to any step)
// ============================================
const StepIndicator = ({ currentStep, onStepClick }) => {
  const steps = [
    { num: 1, label: 'Upload' },
    { num: 2, label: 'Mitigation' },
    { num: 3, label: 'Repairs' },
    { num: 4, label: 'Review' },
  ];

  return (
    <div className="flex items-center justify-center gap-2 py-3">
      {steps.map((step, i) => (
        <div key={step.num} className="flex items-center">
          <button
            onClick={() => onStepClick?.(step.num)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer hover:opacity-80 ${
              step.num === currentStep ? 'ring-2 ring-offset-2 ring-offset-black' : ''
            }`}
            style={{
              background: step.num <= currentStep ? colors.yellow : colors.inputBg,
              color: step.num <= currentStep ? colors.black : colors.textMuted,
              ringColor: colors.yellow,
            }}
          >
            {step.num < currentStep ? <Check size={12} /> : step.num}
            <span className="hidden sm:inline">{step.label}</span>
          </button>
          {i < steps.length - 1 && (
            <ChevronRight size={16} className="mx-1" style={{ color: colors.textMuted }} />
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================
// ESTIMATE TOTAL DISPLAY
// ============================================
const EstimateTotal = ({ totals }) => {
  const grandTotal = totals?.grandTotal || 0;
  return (
    <div className="text-right">
      <p className="text-xs" style={{ color: colors.textMuted }}>Estimate</p>
      <p className="text-lg font-bold" style={{ color: colors.green }}>
        ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
      </p>
    </div>
  );
};

// ============================================
// CLARIFY PANEL (Slide-over with image support)
// ============================================
const ClarifyPanel = ({ isOpen, onClose, estimate, onEstimateUpdate }) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachedImages, setAttachedImages] = useState([]); // Array of { file, preview }
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);

  const thread = estimate?.clarificationThread || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [isOpen, thread.length]);

  // Handle file selection
  const handleFileSelect = (files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    const newImages = imageFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setAttachedImages(prev => [...prev, ...newImages].slice(0, 4)); // Max 4 images
  };

  // Handle paste event for images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    if (imageItems.length === 0) return;

    e.preventDefault();
    const files = imageItems.map(item => item.getAsFile()).filter(Boolean);
    handleFileSelect(files);
  };

  // Remove attached image
  const removeImage = (index) => {
    setAttachedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Convert image to base64
  const imageToBase64 = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
  };

  const handleSend = async () => {
    if ((!input.trim() && attachedImages.length === 0) || loading) return;

    const userMessage = input.trim();
    setInput('');
    setLoading(true);

    // Convert images to base64 for storage and API
    const imageData = await Promise.all(
      attachedImages.map(async (img) => ({
        base64: await imageToBase64(img.file),
        name: img.file.name,
      }))
    );

    // Clear attached images
    attachedImages.forEach(img => URL.revokeObjectURL(img.preview));
    setAttachedImages([]);

    // Add user message to thread (with image references)
    const newThread = [
      ...thread,
      {
        id: Date.now(),
        type: 'user',
        content: userMessage || (imageData.length > 0 ? `[Attached ${imageData.length} image(s)]` : ''),
        timestamp: new Date().toISOString(),
        images: imageData.map(img => img.base64), // Store base64 for display
      }
    ];
    onEstimateUpdate({ clarificationThread: newThread });

    try {
      // Pass images to processClarification
      const result = await processClarification(
        estimate,
        thread,
        userMessage || 'Please analyze the attached image(s)',
        imageData.map(img => ({ url: img.base64 })) // Pass as image array
      );

      // Add AI response
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: result.message || result,
        timestamp: new Date().toISOString(),
        changes: result.changes || [],
      };

      const updatedThread = [...newThread, aiMessage];

      // Apply any changes from AI
      const updates = { clarificationThread: updatedThread };
      if (result.changes && result.changes.length > 0) {
        result.changes.forEach(change => {
          if (change.field && change.value !== undefined) {
            // Handle nested fields like mitigation.values.sqft
            const parts = change.field.split('.');
            if (parts.length === 1) {
              updates[change.field] = change.value;
            } else if (parts[0] === 'mitigation' && parts[1] === 'values') {
              updates.mitigation = {
                ...estimate.mitigation,
                values: { ...estimate.mitigation?.values, [parts[2]]: change.value }
              };
            }
          }
        });
      }

      onEstimateUpdate(updates);
    } catch (error) {
      console.error('Clarification error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: 'Sorry, I had trouble processing that. Please try again.',
        timestamp: new Date().toISOString(),
      };
      onEstimateUpdate({ clarificationThread: [...newThread, errorMessage] });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative w-full max-w-md h-full flex flex-col"
        style={{ background: colors.cardBg }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: colors.border }}>
          <div>
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>Estimate Clarifications</h3>
            <p className="text-xs" style={{ color: colors.textMuted }}>{thread.length} messages · Paste or attach images</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10">
            <X size={20} style={{ color: colors.textMuted }} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {thread.length === 0 && (
            <div className="text-center py-8">
              <MessageCircle size={32} className="mx-auto mb-2" style={{ color: colors.textMuted }} />
              <p className="text-sm" style={{ color: colors.textMuted }}>
                Ask questions or make corrections to your estimate
              </p>
              <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                Tip: Paste screenshots directly (Cmd/Ctrl+V)
              </p>
            </div>
          )}
          {thread.map(msg => (
            <div
              key={msg.id}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] p-3 rounded-xl ${msg.type === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                style={{
                  background: msg.type === 'user' ? colors.yellow : colors.inputBg,
                  color: msg.type === 'user' ? colors.black : colors.textPrimary,
                }}
              >
                {/* Display attached images */}
                {msg.images && msg.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {msg.images.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Attachment ${i + 1}`}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                {msg.changes && msg.changes.length > 0 && (
                  <div className="mt-2 p-2 rounded text-xs" style={{ background: 'rgba(0,0,0,0.2)' }}>
                    <p className="font-medium mb-1">Changes Applied:</p>
                    {msg.changes.map((c, i) => (
                      <p key={i}>• {c.field}: {c.from} → {c.to}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="p-3 rounded-xl" style={{ background: colors.inputBg }}>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: colors.yellow }} />
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: colors.yellow, animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: colors.yellow, animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Attached Images Preview */}
        {attachedImages.length > 0 && (
          <div className="px-4 py-2 border-t flex gap-2 overflow-x-auto" style={{ borderColor: colors.border }}>
            {attachedImages.map((img, i) => (
              <div key={i} className="relative flex-shrink-0">
                <img
                  src={img.preview}
                  alt={`Attachment ${i + 1}`}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ background: colors.red }}
                >
                  <X size={12} style={{ color: '#fff' }} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t" style={{ borderColor: colors.border }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={e => handleFileSelect(e.target.files)}
            className="hidden"
          />
          <div className="flex gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-xl"
              style={{ background: colors.inputBg }}
              title="Attach image"
            >
              <Upload size={18} style={{ color: colors.textMuted }} />
            </button>
            <input
              ref={textInputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              onPaste={handlePaste}
              placeholder="Type or paste screenshot..."
              className="flex-1 px-4 py-2 rounded-xl text-sm outline-none"
              style={{ background: colors.inputBg, color: colors.textPrimary }}
            />
            <button
              onClick={handleSend}
              disabled={((!input.trim() && attachedImages.length === 0) || loading)}
              className="p-2 rounded-xl disabled:opacity-50"
              style={{ background: colors.yellow }}
            >
              <Send size={18} style={{ color: colors.black }} />
            </button>
          </div>
          <p className="text-xs mt-2 text-center" style={{ color: colors.textMuted }}>
            Cmd/Ctrl+V to paste screenshots
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================
// LIST VIEW
// ============================================
const ListView = ({ estimates, onSelect, onNew }) => {
  const [search, setSearch] = useState('');

  const filtered = estimates.filter(e =>
    (e.propertyAddress || '').toLowerCase().includes(search.toLowerCase()) ||
    (e.estimateName || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col" style={{ background: colors.black }}>
      <div className="p-4 border-b" style={{ borderColor: colors.border }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.textPrimary }}>Insurance Estimates</h1>
            <p className="text-sm" style={{ color: colors.textMuted }}>Water damage loss estimates</p>
          </div>
          <Button onClick={onNew}>
            <Plus size={18} />
            New Estimate
          </Button>
        </div>
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: colors.textMuted }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search estimates..."
            className="w-full pl-10 pr-4 py-2 rounded-xl text-sm outline-none"
            style={{ background: colors.inputBg, color: colors.textPrimary }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <FileText size={48} className="mx-auto mb-3" style={{ color: colors.textMuted }} />
            <p style={{ color: colors.textMuted }}>
              {search ? 'No estimates match your search' : 'No estimates yet'}
            </p>
            {!search && (
              <Button onClick={onNew} variant="secondary" className="mt-4">
                <Plus size={16} /> Create First Estimate
              </Button>
            )}
          </div>
        )}
        {filtered.map(estimate => (
          <button
            key={estimate.id}
            onClick={() => onSelect(estimate)}
            className="w-full p-4 rounded-xl text-left transition-all hover:scale-[1.01]"
            style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="font-semibold" style={{ color: colors.textPrimary }}>
                  {estimate.estimateName || 'Untitled Estimate'}
                </p>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  {estimate.propertyAddress || 'No address'}
                </p>
              </div>
              <div className="text-right">
                <span
                  className="text-xs px-2 py-1 rounded-full"
                  style={{
                    background: estimate.status === 'complete' ? colors.greenDim : colors.yellowDim,
                    color: estimate.status === 'complete' ? colors.green : colors.yellow,
                  }}
                >
                  {estimate.status === 'complete' ? 'Complete' : 'Draft'}
                </span>
                {estimate.currentVersion > 0 && (
                  <p className="text-xs mt-1" style={{ color: colors.textMuted }}>v{estimate.currentVersion}</p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs" style={{ color: colors.textMuted }}>
                {estimate.updatedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
              </p>
              <p className="font-semibold" style={{ color: colors.green }}>
                ${(estimate.totals?.grandTotal || 0).toLocaleString()}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================
// STEP 1: UPLOAD
// ============================================
const Step1Upload = ({ estimate, onUpdate, onNext }) => {
  const [parsing, setParsing] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParsing(true);

    try {
      // For now, we'll read the file content
      // In production, you'd upload to Firebase Storage first
      let parsedData;

      if (file.type.includes('image') || file.type === 'application/pdf') {
        // Use vision parsing for images/PDFs
        const reader = new FileReader();
        const base64 = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        parsedData = await parseEstimateWithVision([{ page: 1, image: base64 }], file.name);
      } else {
        // Use text parsing for other files
        const text = await file.text();
        parsedData = await parseEstimateDocument(text, file.name);
      }

      onUpdate({
        upload: {
          fileName: file.name,
          uploadedAt: new Date().toISOString(),
          parsedData,
          rawText: parsedData.rawText || '',
        },
      });
    } catch (error) {
      console.error('Parse error:', error);
      alert('Failed to parse document. Please try again.');
    } finally {
      setParsing(false);
    }
  };

  const hasUpload = !!estimate.upload?.parsedData;
  const parsedData = estimate.upload?.parsedData || {};
  const lineItems = parsedData.lineItems || [];
  const mitigationItems = lineItems.filter(i => i.category === 'mitigation');
  const repairItems = lineItems.filter(i => i.category === 'repair');

  return (
    <div className="space-y-6">
      {/* Property Info */}
      <Card className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
            Property Address
          </label>
          <input
            type="text"
            value={estimate.propertyAddress || ''}
            onChange={e => onUpdate({ propertyAddress: e.target.value })}
            placeholder="123 Main Street, Austin TX 78701"
            className="w-full px-4 py-2 rounded-xl text-sm outline-none"
            style={{ background: colors.inputBg, color: colors.textPrimary }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>
            Estimate Name
          </label>
          <input
            type="text"
            value={estimate.estimateName || ''}
            onChange={e => onUpdate({ estimateName: e.target.value })}
            placeholder="Water Loss - Feb 2025"
            className="w-full px-4 py-2 rounded-xl text-sm outline-none"
            style={{ background: colors.inputBg, color: colors.textPrimary }}
          />
        </div>
      </Card>

      {/* File Upload */}
      {!hasUpload ? (
        <Card className="p-8">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xls,.xlsx,.csv,image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={parsing}
            className="w-full flex flex-col items-center justify-center p-8 rounded-xl border-2 border-dashed transition-all hover:border-solid"
            style={{ borderColor: colors.border }}
          >
            {parsing ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mb-4" style={{ borderColor: colors.yellow }} />
                <p style={{ color: colors.textPrimary }}>Parsing document...</p>
              </>
            ) : (
              <>
                <Upload size={48} className="mb-4" style={{ color: colors.yellow }} />
                <p className="font-semibold mb-1" style={{ color: colors.textPrimary }}>
                  Drop your SNSHN estimate here
                </p>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  PDF, Excel, CSV, or screenshot
                </p>
              </>
            )}
          </button>
        </Card>
      ) : (
        <>
          {/* Parsed Results */}
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: colors.greenDim }}>
                  <CheckCircle size={20} style={{ color: colors.green }} />
                </div>
                <div>
                  <p className="font-medium" style={{ color: colors.textPrimary }}>{estimate.upload.fileName}</p>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    {lineItems.length} line items · ${parsedData.total?.toLocaleString() || 0} total
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRaw(!showRaw)}
                className="text-xs px-3 py-1 rounded-lg"
                style={{ background: colors.inputBg, color: colors.textMuted }}
              >
                {showRaw ? 'Hide' : 'View'} Raw
              </button>
            </div>

            {showRaw && (
              <pre
                className="text-xs p-3 rounded-lg overflow-auto max-h-48 mb-4"
                style={{ background: colors.inputBg, color: colors.textSecondary }}
              >
                {JSON.stringify(parsedData, null, 2)}
              </pre>
            )}

            <div className="space-y-2">
              <div className="flex justify-between p-3 rounded-lg" style={{ background: colors.inputBg }}>
                <span style={{ color: colors.textMuted }}>Repairs ({repairItems.length} items)</span>
                <span style={{ color: colors.textPrimary }}>${parsedData.repairTotal?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between p-3 rounded-lg" style={{ background: colors.inputBg }}>
                <span style={{ color: colors.textMuted }}>Mitigation ({mitigationItems.length} items)</span>
                <span style={{ color: colors.textPrimary }}>${parsedData.mitigationTotal?.toLocaleString() || 0}</span>
              </div>
              <div className="flex justify-between p-3 rounded-lg font-semibold" style={{ background: colors.yellowDim }}>
                <span style={{ color: colors.yellow }}>Upload Total</span>
                <span style={{ color: colors.yellow }}>${parsedData.total?.toLocaleString() || 0}</span>
              </div>
            </div>
          </Card>

          {/* What We'll Do */}
          <Card className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={20} style={{ color: colors.orange }} />
              <p className="text-sm" style={{ color: colors.textPrimary }}>
                Mitigation appears incomplete. We'll help you build a proper scope.
              </p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: colors.inputBg }}>
              <p className="text-sm font-medium mb-2" style={{ color: colors.textPrimary }}>What we'll do:</p>
              <ul className="text-sm space-y-1" style={{ color: colors.textSecondary }}>
                <li>• Expand mitigation to proper market-rate line items</li>
                <li>• Apply labor/material breakdowns to all repairs</li>
                <li>• Add O&P (20%)</li>
              </ul>
            </div>
          </Card>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <div />
        <Button onClick={onNext} disabled={!hasUpload || !estimate.propertyAddress}>
          Continue <ChevronRight size={18} />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 2: MITIGATION
// ============================================
const Step2Mitigation = ({ estimate, onUpdate, onBack, onNext }) => {
  const values = estimate.mitigation?.values || getDefaultMitigationValues();
  const rates = estimate.mitigation?.rates || getDefaultRates();
  const approvals = estimate.mitigation?.approvals || {};

  // Calculate recommendations based on sqft
  const recommendations = calculateEquipmentRecommendations(values.sqft || 0);

  // Update values helper
  const updateValues = (newValues) => {
    onUpdate({
      mitigation: {
        ...estimate.mitigation,
        values: { ...values, ...newValues },
      },
    });
  };

  // Update rates helper
  const updateRates = (newRates) => {
    onUpdate({
      mitigation: {
        ...estimate.mitigation,
        rates: { ...rates, ...newRates },
      },
    });
  };

  // Toggle approval helper
  const toggleApproval = (field) => {
    onUpdate({
      mitigation: {
        ...estimate.mitigation,
        approvals: { ...approvals, [field]: !approvals[field] },
      },
    });
  };

  // Calculate line items and totals
  const lineItems = calculateMitigationLineItems(values, rates);
  const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);

  // Update totals when line items change
  useEffect(() => {
    onUpdate({
      mitigation: {
        ...estimate.mitigation,
        lineItems,
        subtotal,
      },
    });
  }, [JSON.stringify(lineItems)]);

  const allApproved = approvals.sqft && approvals.dehumidifiers && approvals.airMovers;

  // Number input with +/- buttons
  const NumberInput = ({ label, value, onChange, recommendation, approved, onApprove, min = 0, max = 999 }) => (
    <div className="p-4 rounded-xl" style={{ background: colors.inputBg }}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium" style={{ color: colors.textPrimary }}>{label}</span>
        {onApprove && (
          <button
            onClick={onApprove}
            className={`text-xs px-3 py-1 rounded-full flex items-center gap-1 ${approved ? 'opacity-50' : ''}`}
            style={{
              background: approved ? colors.greenDim : colors.yellowDim,
              color: approved ? colors.green : colors.yellow,
            }}
          >
            {approved ? <Check size={12} /> : null}
            {approved ? 'Approved' : 'Approve'}
          </button>
        )}
      </div>
      {recommendation && (
        <p className="text-xs mb-2" style={{ color: colors.textMuted }}>{recommendation}</p>
      )}
      <div className="flex items-center gap-3">
        <button
          onClick={() => onChange(Math.max(min, value - 1))}
          className="p-2 rounded-lg"
          style={{ background: colors.cardBg }}
        >
          <Minus size={16} style={{ color: colors.textPrimary }} />
        </button>
        <input
          type="number"
          value={value}
          onChange={e => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || 0)))}
          className="w-20 text-center text-xl font-bold bg-transparent outline-none"
          style={{ color: colors.textPrimary }}
        />
        <button
          onClick={() => onChange(Math.min(max, value + 1))}
          className="p-2 rounded-lg"
          style={{ background: colors.cardBg }}
        >
          <Plus size={16} style={{ color: colors.textPrimary }} />
        </button>
      </div>
    </div>
  );

  // Rate slider
  const RateSlider = ({ rateKey, config }) => (
    <div className="py-2">
      <div className="flex justify-between mb-1">
        <span className="text-sm" style={{ color: colors.textMuted }}>{config.label}</span>
        <span className="text-sm font-medium" style={{ color: colors.textPrimary }}>
          ${rates[rateKey]?.toFixed(2)}/{config.unit.replace('per ', '')}
        </span>
      </div>
      <input
        type="range"
        min={config.min}
        max={config.max}
        step={config.max > 10 ? 5 : 0.05}
        value={rates[rateKey] || config.default}
        onChange={e => updateRates({ [rateKey]: parseFloat(e.target.value) })}
        className="w-full"
      />
      <div className="flex justify-between text-xs" style={{ color: colors.textMuted }}>
        <span>${config.min}</span>
        <span>${config.default} (market)</span>
        <span>${config.max}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* AI Estimates */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold" style={{ color: colors.textPrimary }}>AI-Estimated Values</h3>
          <span className="text-xs" style={{ color: colors.textMuted }}>Approve each to continue</span>
        </div>

        <div className="space-y-3">
          <NumberInput
            label="Affected Square Footage"
            value={values.sqft || 0}
            onChange={v => updateValues({ sqft: v })}
            recommendation="Based on repair scope"
            approved={approvals.sqft}
            onApprove={() => toggleApproval('sqft')}
            max={10000}
          />

          <NumberInput
            label="LGR Dehumidifiers (180 PPD)"
            value={values.dehumidifiers || 0}
            onChange={v => updateValues({ dehumidifiers: v })}
            recommendation={`Recommended: 1 per 100-125 SF → ${recommendations.dehumidifiers} units`}
            approved={approvals.dehumidifiers}
            onApprove={() => toggleApproval('dehumidifiers')}
            max={50}
          />

          <NumberInput
            label="Air Movers"
            value={values.airMovers || 0}
            onChange={v => updateValues({ airMovers: v })}
            recommendation={`Recommended: 2-3 per dehu → ${recommendations.airMovers} units`}
            approved={approvals.airMovers}
            onApprove={() => toggleApproval('airMovers')}
            max={100}
          />
        </div>

        {!allApproved && (
          <p className="text-xs text-center mt-4" style={{ color: colors.orange }}>
            Approve all AI estimates above to continue
          </p>
        )}
      </Card>

      {/* Drying & Monitoring */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Drying & Monitoring</h3>

        <NumberInput
          label="Drying Days"
          value={values.dryingDays || 4}
          onChange={v => updateValues({ dryingDays: v, monitoringVisits: v })}
          min={1}
          max={21}
        />

        <div className="mt-3 p-3 rounded-lg" style={{ background: colors.inputBg }}>
          {DRYING_DAYS_GUIDANCE.map(g => (
            <p key={g.range} className="text-xs" style={{ color: colors.textMuted }}>
              • {g.range} days: {g.description}
            </p>
          ))}
        </div>

        <p className="text-sm mt-3" style={{ color: colors.textMuted }}>
          Monitoring Visits: {values.dryingDays || 4} (auto-matched to drying days)
        </p>
      </Card>

      {/* Response Type & Water Category */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Response Type</h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => updateValues({ afterHours: false })}
            className={`p-3 rounded-xl text-center ${!values.afterHours ? 'ring-2' : ''}`}
            style={{
              background: colors.inputBg,
              ringColor: colors.yellow,
            }}
          >
            <p className="font-medium" style={{ color: colors.textPrimary }}>Business Hours</p>
            <p className="text-xs" style={{ color: colors.textMuted }}>Standard</p>
          </button>
          <button
            onClick={() => updateValues({ afterHours: true })}
            className={`p-3 rounded-xl text-center ${values.afterHours ? 'ring-2' : ''}`}
            style={{
              background: colors.inputBg,
              ringColor: colors.yellow,
            }}
          >
            <p className="font-medium" style={{ color: colors.textPrimary }}>After Hours</p>
            <p className="text-xs" style={{ color: colors.green }}>+$350</p>
          </button>
        </div>

        <h3 className="font-semibold mt-6 mb-4" style={{ color: colors.textPrimary }}>Water Category</h3>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(WATER_CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => updateValues({ waterCategory: key })}
              className={`p-3 rounded-xl text-center ${values.waterCategory === key ? 'ring-2' : ''}`}
              style={{
                background: colors.inputBg,
                ringColor: colors.yellow,
              }}
            >
              <p className="font-medium text-sm" style={{ color: colors.textPrimary }}>{cat.label}</p>
              <p className="text-xs" style={{ color: colors.textMuted }}>
                {key === 'cat1' ? 'Clean' : key === 'cat2' ? 'Gray' : 'Black'}
              </p>
            </button>
          ))}
        </div>
        {values.waterCategory !== 'cat1' && (
          <p className="text-xs mt-2" style={{ color: colors.orange }}>
            {WATER_CATEGORIES[values.waterCategory]?.label} adds ${WATER_CATEGORIES[values.waterCategory]?.adjustment}/SF
          </p>
        )}
      </Card>

      {/* Equipment Rates (Collapsible) */}
      <Card className="p-4">
        <details>
          <summary className="cursor-pointer font-semibold" style={{ color: colors.textPrimary }}>
            Equipment Rates (adjust if needed)
          </summary>
          <div className="mt-4 space-y-4">
            {['dehumidifier', 'airMover', 'extraction', 'monitoring', 'delivery', 'containment', 'antimicrobial'].map(key => (
              <RateSlider key={key} rateKey={key} config={MITIGATION_RATES[key]} />
            ))}
          </div>
        </details>
      </Card>

      {/* Line Items Preview */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Mitigation Scope Preview</h3>
        <div className="space-y-2">
          {lineItems.map(item => (
            <div key={item.id} className="flex justify-between text-sm">
              <span style={{ color: colors.textMuted }}>{item.description}</span>
              <span style={{ color: colors.textPrimary }}>${item.total.toLocaleString()}</span>
            </div>
          ))}
          <div className="border-t pt-2 mt-2 flex justify-between font-semibold" style={{ borderColor: colors.border }}>
            <span style={{ color: colors.textPrimary }}>Mitigation Subtotal</span>
            <span style={{ color: colors.green }}>${subtotal.toLocaleString()}</span>
          </div>
        </div>
        {estimate.upload?.parsedData?.mitigationTotal && (
          <p className="text-xs mt-2" style={{ color: colors.green }}>
            vs. Original Upload: ${estimate.upload.parsedData.mitigationTotal.toLocaleString()} → +${(subtotal - estimate.upload.parsedData.mitigationTotal).toLocaleString()}
          </p>
        )}
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeft size={18} /> Back
        </Button>
        <Button onClick={onNext} disabled={!allApproved}>
          Continue <ChevronRight size={18} />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 3: REPAIRS
// ============================================
const Step3Repairs = ({ estimate, onUpdate, onBack, onNext }) => {
  const [analyzing, setAnalyzing] = useState(false);

  const parsedItems = estimate.upload?.parsedData?.lineItems?.filter(i => i.category === 'repair') || [];
  const repairItems = estimate.repairs?.items || parsedItems.map((item, i) => ({
    id: `repair-${i}`,
    sourceRef: `Line ${item.line}`,
    description: item.description,
    amount: item.amount,
    trade: 'general',
    laborPct: 50,
    approved: false,
  }));

  // Initialize repair items if not set
  useEffect(() => {
    if (!estimate.repairs?.items && parsedItems.length > 0) {
      onUpdate({
        repairs: {
          items: repairItems,
          subtotal: repairItems.reduce((s, i) => s + (i.amount || 0), 0),
          laborTotal: 0,
          materialTotal: 0,
        },
      });
    }
  }, []);

  const updateItem = (id, updates) => {
    const newItems = repairItems.map(item =>
      item.id === id ? { ...item, ...updates } : item
    );
    const subtotal = newItems.reduce((s, i) => s + (i.amount || 0), 0);
    const laborTotal = newItems.reduce((s, i) => s + ((i.amount || 0) * (i.laborPct || 50) / 100), 0);
    const materialTotal = subtotal - laborTotal;

    onUpdate({
      repairs: {
        items: newItems,
        subtotal,
        laborTotal,
        materialTotal,
      },
    });
  };

  const approveAll = () => {
    const newItems = repairItems.map(item => ({ ...item, approved: true }));
    const subtotal = newItems.reduce((s, i) => s + (i.amount || 0), 0);
    const laborTotal = newItems.reduce((s, i) => s + ((i.amount || 0) * (i.laborPct || 50) / 100), 0);
    onUpdate({
      repairs: {
        items: newItems,
        subtotal,
        laborTotal,
        materialTotal: subtotal - laborTotal,
      },
    });
  };

  const analyzeItems = async () => {
    setAnalyzing(true);
    try {
      const results = await analyzeRepairLineItems(repairItems);
      if (results?.items) {
        const newItems = repairItems.map((item, i) => ({
          ...item,
          trade: results.items[i]?.trade || item.trade,
          laborPct: results.items[i]?.laborPct || LABOR_SPLITS[results.items[i]?.trade]?.labor || 50,
        }));
        const subtotal = newItems.reduce((s, i) => s + (i.amount || 0), 0);
        const laborTotal = newItems.reduce((s, i) => s + ((i.amount || 0) * (i.laborPct || 50) / 100), 0);
        onUpdate({
          repairs: {
            items: newItems,
            subtotal,
            laborTotal,
            materialTotal: subtotal - laborTotal,
          },
        });
      }
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const approvedCount = repairItems.filter(i => i.approved).length;
  const subtotal = estimate.repairs?.subtotal || 0;
  const laborTotal = estimate.repairs?.laborTotal || 0;
  const materialTotal = estimate.repairs?.materialTotal || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>Repair Line Items</h3>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              {approvedCount}/{repairItems.length} approved
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={analyzeItems} disabled={analyzing}>
              {analyzing ? 'Analyzing...' : 'Auto-Detect Trades'}
            </Button>
            <Button variant="secondary" size="sm" onClick={approveAll}>
              Approve All
            </Button>
          </div>
        </div>
      </Card>

      {/* Line Items */}
      <div className="space-y-3">
        {repairItems.map(item => (
          <Card key={item.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded" style={{ background: colors.inputBg, color: colors.textMuted }}>
                    {item.sourceRef}
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded"
                    style={{
                      background: item.approved ? colors.greenDim : colors.yellowDim,
                      color: item.approved ? colors.green : colors.yellow,
                    }}
                  >
                    {item.approved ? 'Approved' : 'Review'}
                  </span>
                </div>
                <p className="font-medium mt-1" style={{ color: colors.textPrimary }}>{item.description}</p>
              </div>
              <p className="font-semibold" style={{ color: colors.textPrimary }}>${item.amount?.toLocaleString()}</p>
            </div>

            {/* Trade Selection */}
            <div className="mb-3">
              <label className="text-xs block mb-1" style={{ color: colors.textMuted }}>Trade</label>
              <select
                value={item.trade}
                onChange={e => {
                  const trade = e.target.value;
                  const laborPct = LABOR_SPLITS[trade]?.labor || 50;
                  updateItem(item.id, { trade, laborPct });
                }}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{ background: colors.inputBg, color: colors.textPrimary }}
              >
                {Object.entries(LABOR_SPLITS).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>

            {/* Labor/Material Split */}
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span style={{ color: colors.textMuted }}>
                  Labor ({item.laborPct}%) - ${((item.amount || 0) * item.laborPct / 100).toLocaleString()}
                </span>
                <span style={{ color: colors.textMuted }}>
                  Material ({100 - item.laborPct}%) - ${((item.amount || 0) * (100 - item.laborPct) / 100).toLocaleString()}
                </span>
              </div>
              <input
                type="range"
                min={10}
                max={95}
                value={item.laborPct}
                onChange={e => updateItem(item.id, { laborPct: parseInt(e.target.value) })}
                className="w-full"
              />
            </div>

            <Button
              variant={item.approved ? 'secondary' : 'primary'}
              size="sm"
              className="mt-3"
              onClick={() => updateItem(item.id, { approved: !item.approved })}
            >
              {item.approved ? 'Unapprove' : 'Approve'}
            </Button>
          </Card>
        ))}
      </div>

      {/* Summary */}
      <Card className="p-4">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span style={{ color: colors.textMuted }}>Repairs Subtotal</span>
            <span style={{ color: colors.textPrimary }}>${subtotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span style={{ color: colors.textMuted }}>Labor Total</span>
            <span style={{ color: colors.textSecondary }}>${laborTotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span style={{ color: colors.textMuted }}>Material Total</span>
            <span style={{ color: colors.textSecondary }}>${materialTotal.toLocaleString()}</span>
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeft size={18} /> Back
        </Button>
        <Button onClick={onNext} disabled={approvedCount === 0}>
          Continue <ChevronRight size={18} />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 4: REVIEW
// ============================================
const Step4Review = ({ estimate, onUpdate, onBack, onGenerate, generating }) => {
  const mitigationTotal = estimate.mitigation?.subtotal || 0;
  const repairsTotal = estimate.repairs?.subtotal || 0;
  const subtotal = mitigationTotal + repairsTotal;
  const oAndPPercent = estimate.totals?.oAndPPercent || FORMULAS.oAndPPercent;
  const oAndP = subtotal * (oAndPPercent / 100);
  const grandTotal = subtotal + oAndP;

  const uploadTotal = estimate.upload?.parsedData?.total || 0;
  const increase = grandTotal - uploadTotal;
  const increasePercent = uploadTotal > 0 ? ((increase / uploadTotal) * 100).toFixed(0) : 0;

  // Health checks
  const healthChecks = [
    {
      pass: (estimate.mitigation?.values?.dehumidifiers || 0) > 0,
      label: 'Equipment counts match industry standards',
    },
    {
      pass: (estimate.mitigation?.values?.dryingDays || 0) >= 3 && (estimate.mitigation?.values?.dryingDays || 0) <= 14,
      label: 'Drying days within normal range',
    },
    {
      pass: (estimate.mitigation?.values?.monitoringVisits || 0) === (estimate.mitigation?.values?.dryingDays || 0),
      label: 'Monitoring visits = drying days',
    },
    {
      pass: estimate.mitigation?.values?.afterHours || true,
      label: estimate.mitigation?.values?.afterHours ? 'After-hours response fee included' : 'Standard response time',
    },
  ];

  // Documentation checklist
  const docs = estimate.documentation || {};
  const updateDoc = (field, value) => {
    onUpdate({
      documentation: { ...docs, [field]: value },
    });
  };

  // Update totals
  useEffect(() => {
    onUpdate({
      totals: {
        mitigation: mitigationTotal,
        repairs: repairsTotal,
        subtotal,
        oAndP,
        oAndPPercent,
        grandTotal,
      },
    });
  }, [mitigationTotal, repairsTotal, oAndPPercent]);

  return (
    <div className="space-y-6">
      {/* Comparison */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Estimate Comparison</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="p-4 rounded-xl text-center" style={{ background: colors.inputBg }}>
            <p className="text-xs" style={{ color: colors.textMuted }}>Internal Est</p>
            <p className="text-2xl font-bold" style={{ color: colors.textPrimary }}>${uploadTotal.toLocaleString()}</p>
          </div>
          <div className="p-4 rounded-xl text-center" style={{ background: colors.greenDim }}>
            <p className="text-xs" style={{ color: colors.green }}>Insurance Est</p>
            <p className="text-2xl font-bold" style={{ color: colors.green }}>${grandTotal.toLocaleString()}</p>
          </div>
        </div>
        <p className="text-center font-medium" style={{ color: colors.green }}>
          +${increase.toLocaleString()} additional recovery (+{increasePercent}%)
        </p>
      </Card>

      {/* Summary Breakdown */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Summary Breakdown</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span style={{ color: colors.textMuted }}>Mitigation</span>
            <span style={{ color: colors.textPrimary }}>${mitigationTotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: colors.textMuted }}>Repairs</span>
            <span style={{ color: colors.textPrimary }}>${repairsTotal.toLocaleString()}</span>
          </div>
          <div className="border-t pt-2 mt-2 flex justify-between" style={{ borderColor: colors.border }}>
            <span style={{ color: colors.textMuted }}>Subtotal</span>
            <span style={{ color: colors.textPrimary }}>${subtotal.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: colors.textMuted }}>O&P ({oAndPPercent}%)</span>
            <span style={{ color: colors.textPrimary }}>${oAndP.toLocaleString()}</span>
          </div>
          <div className="border-t pt-2 mt-2 flex justify-between font-bold" style={{ borderColor: colors.border }}>
            <span style={{ color: colors.textPrimary }}>GRAND TOTAL</span>
            <span style={{ color: colors.green }}>${grandTotal.toLocaleString()}</span>
          </div>
        </div>
      </Card>

      {/* Health Check */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Estimate Health Check</h3>
        <div className="space-y-2">
          {healthChecks.map((check, i) => (
            <div key={i} className="flex items-center gap-2">
              {check.pass ? (
                <CheckCircle size={16} style={{ color: colors.green }} />
              ) : (
                <AlertTriangle size={16} style={{ color: colors.orange }} />
              )}
              <span className="text-sm" style={{ color: colors.textSecondary }}>{check.label}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Documentation Checklist */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Documentation Checklist</h3>
        <div className="space-y-2">
          {[
            { key: 'damagePhotos', label: 'Photos of damage (before mitigation)', required: true },
            { key: 'equipmentPhotos', label: 'Photos of equipment placement', required: true },
            { key: 'moistureLogs', label: 'Daily moisture reading logs', required: true },
            { key: 'completionPhotos', label: 'Photos of completed repairs', required: false },
          ].map(item => (
            <label key={item.key} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={docs[item.key] || false}
                onChange={e => updateDoc(item.key, e.target.checked)}
                className="w-5 h-5 rounded"
              />
              <span className="text-sm" style={{ color: colors.textSecondary }}>
                {item.label}
                {item.required && <span style={{ color: colors.red }}> *Required</span>}
              </span>
            </label>
          ))}
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeft size={18} /> Back
        </Button>
        <Button onClick={onGenerate} disabled={generating}>
          {generating ? 'Generating...' : 'Generate Final Estimate'}
        </Button>
      </div>

      <p className="text-xs text-center" style={{ color: colors.textMuted }}>
        This will create Version {(estimate.currentVersion || 0) + 1}. You can make changes later.
      </p>
    </div>
  );
};

// ============================================
// COMPLETE VIEW
// ============================================
const CompleteView = ({ estimate, onEdit, onBack }) => {
  const [downloading, setDownloading] = useState(null);

  const handleDownloadPDF = async () => {
    setDownloading('pdf');
    try {
      await generatePDF(estimate);
    } catch (error) {
      console.error('PDF error:', error);
      alert('Failed to generate PDF');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadXLS = async () => {
    setDownloading('xls');
    try {
      await generateXLS(estimate);
    } catch (error) {
      console.error('XLS error:', error);
      alert('Failed to generate XLS');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-full" style={{ background: colors.greenDim }}>
            <CheckCircle size={32} style={{ color: colors.green }} />
          </div>
          <div>
            <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>Estimate Complete</h2>
            <p className="text-sm" style={{ color: colors.textMuted }}>
              Version {estimate.currentVersion} · Generated {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        <p className="text-3xl font-bold mb-6" style={{ color: colors.green }}>
          ${(estimate.totals?.grandTotal || 0).toLocaleString()}
        </p>

        <div className="flex gap-3">
          <Button onClick={handleDownloadPDF} disabled={downloading === 'pdf'}>
            <Download size={18} />
            {downloading === 'pdf' ? 'Generating...' : 'Download PDF'}
          </Button>
          <Button variant="secondary" onClick={handleDownloadXLS} disabled={downloading === 'xls'}>
            <Download size={18} />
            {downloading === 'xls' ? 'Generating...' : 'Download XLS'}
          </Button>
        </div>
      </Card>

      {/* Version History */}
      {estimate.versions?.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-4" style={{ color: colors.textPrimary }}>Version History</h3>
          <div className="space-y-2">
            {estimate.versions.map(v => (
              <div
                key={v.version}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: colors.inputBg }}
              >
                <div>
                  <span className="font-medium" style={{ color: colors.textPrimary }}>v{v.version}</span>
                  <span className="text-sm ml-2" style={{ color: colors.textMuted }}>
                    {new Date(v.generatedAt).toLocaleDateString()}
                  </span>
                </div>
                <span style={{ color: colors.green }}>${v.totals?.grandTotal?.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex justify-between pt-4">
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeft size={18} /> Back to List
        </Button>
        <Button variant="secondary" onClick={onEdit}>
          <Edit3 size={18} /> Edit Estimate
        </Button>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
export default function InsuranceEstimatorScreen({ setActive }) {
  const { insuranceEstimates, loading, addInsuranceEstimate, updateInsuranceEstimate } = useInsuranceData();
  const { user } = useAuth();

  const [view, setView] = useState('list'); // list, edit, complete
  const [currentEstimate, setCurrentEstimate] = useState(null);
  const [clarifyOpen, setClarifyOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Auto-save with debounce
  const saveTimeoutRef = useRef(null);

  const saveEstimate = useCallback(async (updates) => {
    if (!currentEstimate?.id) return;

    setSaveStatus('saving');
    try {
      await updateInsuranceEstimate(currentEstimate.id, updates);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
    }
  }, [currentEstimate?.id, updateInsuranceEstimate]);

  const handleUpdate = useCallback((updates) => {
    setCurrentEstimate(prev => ({ ...prev, ...updates }));

    // Debounced save
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveEstimate(updates);
    }, 500);
  }, [saveEstimate]);

  // Create new estimate
  const handleNew = async () => {
    const newEstimate = createEmptyEstimate();
    newEstimate.createdBy = user?.email || 'unknown';

    try {
      const id = await addInsuranceEstimate(newEstimate);
      setCurrentEstimate({ ...newEstimate, id });
      setView('edit');
    } catch (error) {
      console.error('Create error:', error);
      alert('Failed to create estimate');
    }
  };

  // Select existing estimate
  const handleSelect = (estimate) => {
    setCurrentEstimate(estimate);
    setView(estimate.status === 'complete' ? 'complete' : 'edit');
  };

  // Navigate steps (allow jumping to any step)
  const handleStepClick = (step) => {
    handleUpdate({ currentStep: step });
  };

  const handleNext = () => {
    const nextStep = Math.min((currentEstimate?.currentStep || 1) + 1, 4);
    handleUpdate({ currentStep: nextStep });
  };

  const handleBack = () => {
    const prevStep = Math.max((currentEstimate?.currentStep || 1) - 1, 1);
    handleUpdate({ currentStep: prevStep });
  };

  // Generate final estimate
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const newVersion = {
        version: (currentEstimate.currentVersion || 0) + 1,
        generatedAt: new Date().toISOString(),
        totals: currentEstimate.totals,
      };

      await updateInsuranceEstimate(currentEstimate.id, {
        status: 'complete',
        currentVersion: newVersion.version,
        versions: [...(currentEstimate.versions || []), newVersion],
      });

      setCurrentEstimate(prev => ({
        ...prev,
        status: 'complete',
        currentVersion: newVersion.version,
        versions: [...(prev.versions || []), newVersion],
      }));

      setView('complete');
    } catch (error) {
      console.error('Generate error:', error);
      alert('Failed to generate estimate');
    } finally {
      setGenerating(false);
    }
  };

  // Back to list
  const handleBackToList = () => {
    setCurrentEstimate(null);
    setView('list');
  };

  // Edit from complete view
  const handleEdit = () => {
    setView('edit');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center" style={{ background: colors.black }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto mb-4" style={{ borderColor: colors.yellow }} />
          <p style={{ color: colors.textMuted }}>Loading estimates...</p>
        </div>
      </div>
    );
  }

  // List View
  if (view === 'list') {
    return (
      <ListView
        estimates={insuranceEstimates}
        onSelect={handleSelect}
        onNew={handleNew}
      />
    );
  }

  // Complete View
  if (view === 'complete' && currentEstimate) {
    return (
      <div className="h-full overflow-y-auto" style={{ background: colors.black }}>
        <div className="p-4">
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 mb-4"
            style={{ color: colors.textMuted }}
          >
            <ArrowLeft size={18} /> Back to List
          </button>
          <h1 className="text-xl font-bold mb-1" style={{ color: colors.textPrimary }}>
            {currentEstimate.estimateName || 'Untitled'}
          </h1>
          <p className="text-sm mb-4" style={{ color: colors.textMuted }}>
            {currentEstimate.propertyAddress}
          </p>
        </div>
        <div className="px-4 pb-20">
          <CompleteView
            estimate={currentEstimate}
            onEdit={handleEdit}
            onBack={handleBackToList}
          />
        </div>
      </div>
    );
  }

  // Edit View (Steps)
  if (view === 'edit' && currentEstimate) {
    const step = currentEstimate.currentStep || 1;

    return (
      <div className="h-full flex flex-col" style={{ background: colors.black }}>
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: colors.border }}>
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={handleBackToList}
              className="flex items-center gap-2"
              style={{ color: colors.textMuted }}
            >
              <ArrowLeft size={18} /> List
            </button>
            <div className="flex items-center gap-3">
              {saveStatus && (
                <span className="text-xs" style={{ color: saveStatus === 'saved' ? colors.green : saveStatus === 'error' ? colors.red : colors.yellow }}>
                  {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Error'}
                </span>
              )}
              <EstimateTotal totals={currentEstimate.totals} />
            </div>
          </div>
          <StepIndicator currentStep={step} onStepClick={handleStepClick} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 pb-24">
          {step === 1 && (
            <Step1Upload
              estimate={currentEstimate}
              onUpdate={handleUpdate}
              onNext={handleNext}
            />
          )}
          {step === 2 && (
            <Step2Mitigation
              estimate={currentEstimate}
              onUpdate={handleUpdate}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}
          {step === 3 && (
            <Step3Repairs
              estimate={currentEstimate}
              onUpdate={handleUpdate}
              onBack={handleBack}
              onNext={handleNext}
            />
          )}
          {step === 4 && (
            <Step4Review
              estimate={currentEstimate}
              onUpdate={handleUpdate}
              onBack={handleBack}
              onGenerate={handleGenerate}
              generating={generating}
            />
          )}
        </div>

        {/* Clarify FAB */}
        <button
          onClick={() => setClarifyOpen(true)}
          className="fixed bottom-24 right-4 p-4 rounded-full shadow-lg"
          style={{ background: colors.yellow }}
        >
          <MessageCircle size={24} style={{ color: colors.black }} />
        </button>

        {/* Clarify Panel */}
        <ClarifyPanel
          isOpen={clarifyOpen}
          onClose={() => setClarifyOpen(false)}
          estimate={currentEstimate}
          onEstimateUpdate={handleUpdate}
        />
      </div>
    );
  }

  return null;
}
